// ---------- line-shapes alphabet ----------
// Motifs' vocabulary plus the one shape a half-cell cannot make: a stroke thinner than the
// cell, centred in it.
//
// Why it exists. A subject drawn with an outline carries lines finer than a cell, and a
// half-cell can only say "ink on that side": it plates the stroke against the cell's edge,
// so a whisker comes out shifted by half a cell and twice too thick. Measured on a cartoon
// cat at 70x70, against the intended ink: adding the four centred bars and their quarter
// versions takes the error from 0.0535 to 0.0465 (and from 0.0250 to 0.0208 at a wider
// blur), where doubling the angular resolution (16 directions instead of 8) and the
// cardinal quarter tiers of `motifsFin` each gain nothing at all — same measurement, same
// selector. The gain is the vocabulary, not the method: on `motifs`' own nine shapes, the
// selector below scores the same as the gradient sector it replaces.
//
//   0 empty · 1 full
//   2..9    half cells, same codes and geometry as `motifs`
//   10..13  centred bars covering 1/2 — horizontal, vertical, and the two diagonals
//   14..17  the same four bars, covering 1/4
//
// Every one of them is the cell cut by a line through its centre (the half cells) or by two
// parallel ones (the bars), so a single table of (normal, half-width) describes the lot,
// and one clipper turns it into both the polygon drawn on screen and the coverage the
// quantizer matches the cell's content against. Nothing here restates a shape twice.

import { inkColor } from '../ink';
import { SECTOR } from './motifs';
import type { Alphabet } from './types';

export const NB = 18;
/** Sub-cell samples per side the encoder reads. 4 is what a working canvas of ~240px
 *  gives back on the grids the game actually ships; below 2 the detail says nothing the
 *  grid didn't already say. */
export const SUB = 4;

// ---------- geometry ----------

type Pt = [number, number];

/** Sutherland-Hodgman: keeps the part of `poly` where n·p >= d. */
function clip(poly: Pt[], nx: number, ny: number, d: number): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    const sa = nx * a[0] + ny * a[1] - d;
    const sb = nx * b[0] + ny * b[1] - d;
    if (sa >= 0) out.push(a);
    if (sa >= 0 !== sb >= 0) {
      const t = sa / (sa - sb);
      out.push([a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])]);
    }
  }
  return out;
}

export function polyArea(poly: Pt[]): number {
  let s = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    s += a[0] * b[1] - b[0] * a[1];
  }
  return Math.abs(s) / 2;
}

/** The cut that defines a code: the half `n·(p - centre) >= 0`, or the band around that
 *  same line when `w` is set. Codes 0 (bare paper) and 1 (whole cell) have none. */
interface Cut {
  nx: number;
  ny: number;
  w: number | null;
}

const CUT: (Cut | null)[] = new Array<Cut | null>(NB).fill(null);

// 2..9 — the eight half cells, built from `motifs`' own sector table rather than restated:
// sector k is the direction, in eighths of a turn, the shape points at.
for (let k = 0; k < 8; k++) {
  const a = (k * Math.PI) / 4;
  CUT[SECTOR[k]] = { nx: Math.cos(a), ny: Math.sin(a), w: null };
}

// 10..17 — the four bars, at 1/2 then 1/4 of coverage. Half-width from the coverage: an
// upright bar covering f is f wide, so its half-width is f/2; a diagonal one cuts two
// corner triangles off the square instead, which leaves 1 - (1 - t)² covered for a band
// |x ± y| <= t, hence t = 1 - sqrt(1 - f) and a half-width of t/sqrt(2).
const BAR_ANGLE = [Math.PI / 2, 0, -Math.PI / 4, Math.PI / 4]; // — | \ /
for (let tier = 0; tier < 2; tier++) {
  const f = tier === 0 ? 0.5 : 0.25;
  for (let d = 0; d < 4; d++) {
    const a = BAR_ANGLE[d];
    const w = d < 2 ? f / 2 : (1 - Math.sqrt(1 - f)) / Math.SQRT2;
    CUT[10 + tier * 4 + d] = { nx: Math.cos(a), ny: Math.sin(a), w };
  }
}

/** The part of the axis-aligned box (x, y, size, size) left by `code`, in cell units. */
export function shapeIn(code: number, x: number, y: number, size: number): Pt[] {
  const box: Pt[] = [
    [x, y],
    [x + size, y],
    [x + size, y + size],
    [x, y + size],
  ];
  if (code === 0) return [];
  if (code === 1) return box;
  const cut = CUT[code]!;
  // the line passes through the cell's centre, whatever part of it we are clipping
  const mid = cut.nx * 0.5 + cut.ny * 0.5;
  if (cut.w === null) return clip(box, cut.nx, cut.ny, mid);
  return clip(clip(box, cut.nx, cut.ny, mid - cut.w), -cut.nx, -cut.ny, -mid - cut.w);
}

/** Polygon of each code in the unit cell, and the fraction of the cell it covers. */
const POLY: Pt[][] = Array.from({ length: NB }, (_, code) => shapeIn(code, 0, 0, 1));
export const AREA: number[] = POLY.map(polyArea);

export const motifsTrait: Alphabet = {
  id: 'motifsTrait',
  sheetMode: 'checker',
  label: 'Motifs au trait',
  symbolName: 'motif',
  helpText:
    'Les motifs, plus quatre <b>traits</b> centrés (et leurs versions fines). Un contour plus ' +
    "fin qu'une case ne peut pas se dire avec une demi-case, qui plaque le trait contre le " +
    "bord et l'épaissit : les moustaches, les rayures et les cernes sortent nets ici. La forme " +
    'est choisie sur le <b>contenu</b> de la case, pas sur son seul dégradé.',

  aspect: () => 1,

  // Same reading order as the other shape alphabets: least revealing first, the full cell —
  // which hands over the silhouette on its own — last. See Alphabet.paletteOrder.
  paletteOrder: [14, 15, 16, 17, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 1],

  draw(ctx, x, y, w, h, code) {
    if (!code) return;
    const s = Math.min(w, h);
    const p = POLY[code];
    ctx.fillStyle = inkColor();
    ctx.beginPath();
    ctx.moveTo(x + p[0][0] * s, y + p[0][1] * s);
    for (let i = 1; i < p.length; i++) ctx.lineTo(x + p[i][0] * s, y + p[i][1] * s);
    ctx.closePath();
    ctx.fill();
  },

  // a shape glued to the swatch's edges is illegible: it needs breathing room
  swatch(ctx, w, h, code) {
    motifsTrait.draw(ctx, 3, 3, Math.min(w, h) - 6, Math.min(w, h) - 6, code);
  },
};

/** Coverage of each code, 0..1 — the tests check the drawn geometry against the coverage the
 *  encoder assumes, and the encoder itself scores candidates with it. */
export const traitCoverage: readonly number[] = AREA;
